import type { TemporalWorkflowDocument } from './workflowTypes';

export type OutputRefRenames = Record<string, string>;

type NamedRow = { id?: unknown; name?: unknown };

function readRow(value: unknown): NamedRow | null {
  return value && typeof value === 'object' ? (value as NamedRow) : null;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function collectFieldNameRenames(
  oldFields: unknown,
  newFields: unknown
): OutputRefRenames {
  if (!Array.isArray(oldFields) || !Array.isArray(newFields)) {
    return {};
  }
  const oldNameById = new Map<string, string>();
  for (const raw of oldFields) {
    const row = readRow(raw);
    if (typeof row?.id === 'string' && typeof row.name === 'string') {
      oldNameById.set(row.id, row.name);
    }
  }
  const renames: OutputRefRenames = {};
  for (const raw of newFields) {
    const row = readRow(raw);
    if (typeof row?.id !== 'string' || typeof row.name !== 'string') {
      continue;
    }
    const previous = oldNameById.get(row.id);
    if (previous !== undefined && previous !== row.name && previous !== '') {
      renames[previous] = row.name;
    }
  }
  if (Object.keys(renames).length > 0 || oldNameById.size > 0) {
    return renames;
  }
  return collectPositionalRenames(oldFields, newFields);
}

/**
 * Without stable ids the only safe rename signal is a single name swapped in
 * place: exactly one name gone, exactly one name new, both at the same index.
 * Anything else is a delete plus an unrelated add, and pairing those by index
 * would silently repoint templates at the wrong field.
 */
function collectSingleNameSwap(
  oldNames: string[],
  newNames: string[]
): OutputRefRenames {
  if (oldNames.length !== newNames.length) {
    return {};
  }
  const newNameSet = new Set(newNames);
  const oldNameSet = new Set(oldNames);
  const removed = oldNames.filter((name) => !newNameSet.has(name));
  const added = newNames.filter((name) => !oldNameSet.has(name));
  if (removed.length !== 1 || added.length !== 1) {
    return {};
  }
  const [previous] = removed;
  const [current] = added;
  if (
    previous === '' ||
    oldNames.indexOf(previous) !== newNames.indexOf(current)
  ) {
    return {};
  }
  return { [previous]: current };
}

function collectPositionalRenames(
  oldFields: unknown[],
  newFields: unknown[]
): OutputRefRenames {
  const toNames = (rows: unknown[]): string[] | null => {
    const names = rows.map((raw) => readRow(raw)?.name);
    return names.every((name): name is string => typeof name === 'string')
      ? names
      : null;
  };
  const oldNames = toNames(oldFields);
  const newNames = toNames(newFields);
  if (!oldNames || !newNames) {
    return {};
  }
  return collectSingleNameSwap(oldNames, newNames);
}

export function collectStructuredFieldRenames(
  oldFields: unknown,
  newFields: unknown
): OutputRefRenames {
  const isPlainObject = (v: unknown): v is Record<string, unknown> =>
    typeof v === 'object' && v !== null && !Array.isArray(v);
  if (!isPlainObject(oldFields) || !isPlainObject(newFields)) {
    return {};
  }
  return collectSingleNameSwap(Object.keys(oldFields), Object.keys(newFields));
}

function buildRenameRegExp(
  nodeId: string,
  oldKeys: string[],
  pathPrefix: string
): RegExp | null {
  if (oldKeys.length === 0) {
    return null;
  }
  const alternation = oldKeys
    .slice()
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp)
    .join('|');
  const prefix = pathPrefix ? `${escapeRegExp(pathPrefix)}\\.` : '';
  return new RegExp(
    `(\\{\\{\\s*${escapeRegExp(
      nodeId
    )}\\.output\\.${prefix})(${alternation})(?=[.})\\[\\s])`,
    'g'
  );
}

function renameInValue(
  value: unknown,
  re: RegExp,
  renames: OutputRefRenames
): unknown {
  if (typeof value === 'string') {
    re.lastIndex = 0;
    return value.replace(
      re,
      (_match, prefix: string, key: string) => `${prefix}${renames[key] ?? key}`
    );
  }
  if (Array.isArray(value)) {
    return value.map((item) => renameInValue(item, re, renames));
  }
  if (value && typeof value === 'object') {
    const next: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(
      value as Record<string, unknown>
    )) {
      next[key] = renameInValue(item, re, renames);
    }
    return next;
  }
  return value;
}

export function applyOutputRefRenames(
  doc: TemporalWorkflowDocument,
  nodeId: string,
  renames: OutputRefRenames,
  pathPrefix = ''
): TemporalWorkflowDocument {
  const oldKeys = Object.keys(renames);
  const re = buildRenameRegExp(nodeId, oldKeys, pathPrefix);
  if (!re) {
    return doc;
  }
  return {
    ...doc,
    nodes: renameInValue(
      doc.nodes,
      re,
      renames
    ) as TemporalWorkflowDocument['nodes']
  };
}

function stripInValue(value: unknown, re: RegExp): unknown {
  if (typeof value === 'string') {
    re.lastIndex = 0;
    return value.replace(re, '');
  }
  if (Array.isArray(value)) {
    // A list entry that was nothing but a reference (e.g. an agent's
    // `trigger_files: ['{{form-1.output.file}}']`) is dropped outright rather
    // than left behind as an empty string.
    return value
      .filter(
        (item) =>
          typeof item !== 'string' ||
          item.trim() === '' ||
          (stripInValue(item, re) as string).trim() !== ''
      )
      .map((item) => stripInValue(item, re));
  }
  if (value && typeof value === 'object') {
    const next: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(
      value as Record<string, unknown>
    )) {
      next[key] = stripInValue(item, re);
    }
    return next;
  }
  return value;
}

/**
 * Removes every `{{<nodeId>...}}` template token that points at one of
 * `removedNodeIds` from the remaining nodes' config/input — once a node is
 * deleted, a reference like `{{form-1.output.name}}` can never resolve, so
 * it's dropped instead of being left behind as a dangling variable.
 */
function buildNodeRefRegExp(removedNodeIds: string[]): RegExp {
  const alternation = removedNodeIds
    .slice()
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp)
    .join('|');
  return new RegExp(
    `\\{\\{\\s*(?:${alternation})(?=[.\\s}\\[])[\\s\\S]*?\\}\\}`,
    'g'
  );
}

/** `stripReferencesToNodes` for a single string, e.g. an unsaved code draft. */
export function stripReferencesInText(
  text: string,
  removedNodeIds: string[]
): string {
  if (removedNodeIds.length === 0) {
    return text;
  }
  return text.replace(buildNodeRefRegExp(removedNodeIds), '');
}

export function stripReferencesToNodes(
  doc: TemporalWorkflowDocument,
  removedNodeIds: string[]
): TemporalWorkflowDocument {
  if (removedNodeIds.length === 0) {
    return doc;
  }
  const re = buildNodeRefRegExp(removedNodeIds);
  // Templates live in `config` and also in `input` (e.g. an output block's
  // value is `input.download_url`), so both are cleaned.
  let changed = false;
  const nodes = doc.nodes.map((node) => {
    const config = stripInValue(node.config, re) as typeof node.config;
    const input = stripInValue(node.input, re) as typeof node.input;
    if (
      JSON.stringify(config) === JSON.stringify(node.config) &&
      JSON.stringify(input) === JSON.stringify(node.input)
    ) {
      return node;
    }
    changed = true;
    return { ...node, config, input };
  });
  return changed ? { ...doc, nodes } : doc;
}
